/* component-site-header.js — <site-header>
   Header behaviours (all progressive; the header works with JS dead):
     1. Publishes the sticky header height to the --header-height custom property.
     2. Cart count bubble: subscribes kk:cart:updated and re-reads item_count from
        the returned cart (falls back to KK.cart.state), keeps the <a href=/cart> real.
     3. Cart icon click → opens the cart drawer via kk:drawer:open {id:'cart'} and
        prevents navigation ONLY when JS is active (no inline onclick-return-false)
        AND a <cart-drawer> is actually mounted (it is not, on /cart).
     4. Search button → publishes kk:drawer:open {id:'search'} (predictive-search opens).
     5. Mobile menu: opens/closes an accessible native <dialog> drawer, restores focus. */
(function () {
  'use strict';

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('[data-header-root]') || this;
      this._measure = this._measure.bind(this);
      this._measure();

      // Keep --header-height current across resize / font load.
      window.addEventListener('resize', this._measure, { passive: true });
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this._measure).catch(function () {});
      }

      this._wireCart();
      this._wireSearch();
      this._wireMobileDrawer();
      this._wireSmartHide();
      this._wirePageRestore();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._measure);
      if (this._unsubCart) this._unsubCart();
      if (this._onSmartScroll) window.removeEventListener('scroll', this._onSmartScroll);
      if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
      if (this._onVisible) document.removeEventListener('visibilitychange', this._onVisible);
    }

    /* ── Back/forward-cache resync (owner video 2026-08-19, iOS Safari) ──
       Safari's bfcache resurrects the EXACT frozen page on Back: DOM, JS state, and the
       <html> class list as they were when the user left. Two things go stale:
         1. the cart: items added on the page the user just left are unknown to this page's
            badge + drawer DOM — the user tapped the cart icon and saw "Ihr Warenkorb ist
            leer" while checkout had 3 items;
         2. the smart header: if the user left mid-scroll-down, html.kk-header-hidden is
            still on, scroll position is restored without a scroll event, and the sticky
            header (with the announcement bar inside it) stays slid off-screen — the PDP
            "without a header" in the recording.
       Fix: on pageshow{persisted} — and as belt-and-braces on tab re-focus — drop the
       hidden class, reset the scroll hysteresis, and run KK.cart.reconcile(), which
       re-reads /cart.js and republishes kk:cart:updated so the badge repaints, the drawer
       self-renders its section, and the sticky bar follows. reconcile() never rejects.
       Also runs on a NON-persisted pageshow when the badge disagrees with KK.cart.state
       (WebKit's history restore outside bfcache leaves the same stale DOM). ── */
    _wirePageRestore() {
      var self = this;
      var resync = function (why) {
        document.documentElement.classList.remove('kk-header-hidden');
        if (self._resetSmartHide) self._resetSmartHide();
        if (!(window.KK && window.KK.cart)) return;
        // Instant badge repaint from the in-memory store (no network) so the count is
        // right the moment the page is visible again; the reconcile below then confirms
        // it against /cart.js and repaints the drawer + every other surface.
        var st = window.KK.cart.state;
        if (st && typeof st.item_count === 'number') self._renderCount(st.item_count);
        if (typeof window.KK.cart.reconcile === 'function') window.KK.cart.reconcile();
      };
      this._onPageShow = function (e) {
        if (e && e.persisted) { resync('bfcache'); return; }
        // Not a bfcache restore, but the restored DOM may still be stale vs. the store
        // (observed in WebKit history navigation): badge says one thing, state another.
        var st = window.KK && window.KK.cart && window.KK.cart.state;
        if (st && self.cartCount && typeof st.item_count === 'number') {
          var shown = parseInt(self.cartCount.textContent, 10);
          if (shown !== st.item_count) resync('stale-dom');
        }
      };
      window.addEventListener('pageshow', this._onPageShow);
      // Tab/app switch back on mobile: the cart may have changed in another tab
      // (or the frozen page was restored without firing pageshow on older iOS).
      this._onVisible = function () {
        if (document.visibilityState !== 'visible' || !(window.KK && window.KK.cart)) return;
        // Coalesced (CartStore's own timer) — app/tab switching on a phone fires this
        // constantly; never more than one /cart.js per burst, and never during a
        // mutation already in flight.
        if (typeof window.KK.cart._scheduleReconcile === 'function') window.KK.cart._scheduleReconcile(300);
        else if (window.KK.cart.reconcile) window.KK.cart.reconcile();
      };
      document.addEventListener('visibilitychange', this._onVisible);
    }

    /* ── Smart header: hide on scroll down, reveal on scroll up (owner 2026-07-14 mobile,
       2026-07-15 all viewports). CSS makes the section wrapper sticky; this only flips
       html.kk-header-hidden. 6px hysteresis ignores rubber-banding; the bar never hides
       within the first 120px so it can't blink at the top of the page. rAF-throttled,
       passive. ── */
    _wireSmartHide() {
      var lastY = window.scrollY;
      var raf = 0;
      var root = document.documentElement;
      this._onSmartScroll = function () {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          var y = window.scrollY;
          if (y > lastY + 6 && y > 120) root.classList.add('kk-header-hidden');
          else if (y < lastY - 6 || y <= 120) root.classList.remove('kk-header-hidden');
          lastY = y;
        });
      };
      window.addEventListener('scroll', this._onSmartScroll, { passive: true });
      // Re-arm the hysteresis from the CURRENT scroll position (used after a bfcache
      // restore, where the browser re-applies scrollY without a scroll event — otherwise
      // the first real scroll compares against a stale lastY and can re-hide the header).
      this._resetSmartHide = function () { lastY = window.scrollY; };
    }

    _measure() {
      var h = this.header.getBoundingClientRect().height;
      if (h) document.documentElement.style.setProperty('--header-height', Math.round(h) + 'px');
    }

    /* ── Cart bubble + drawer open ── */
    _wireCart() {
      this.cartLink = this.querySelector('[data-header-cart]');
      this.cartCount = this.querySelector('[data-cart-count]');

      if (this.cartLink && window.KK && window.KK.pubsub) {
        this.cartLink.addEventListener('click', function (e) {
          // Enhance: open the drawer instead of navigating. Middle-click / modifier
          // clicks fall through to the real /cart link.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          // NO DRAWER, NO preventDefault (buy-path audit 2026-07-31): layout/theme.liquid
          // no longer mounts <cart-drawer> on /cart (two live cart surfaces made the page's
          // stale updates[] submittable). Checked per click, not at connect time, because
          // the drawer section can be added/removed by the theme editor. Without this the
          // icon would preventDefault into nothing and become a dead control on /cart.
          if (!document.querySelector('cart-drawer')) return; // let the real /cart link win
          e.preventDefault();
          window.KK.pubsub.publish('kk:drawer:open', { id: 'cart' });
        });
      }

      if (window.KK && window.KK.pubsub) {
        this._unsubCart = window.KK.pubsub.subscribe('kk:cart:updated', function (payload) {
          var count = null;
          if (payload && payload.cart && typeof payload.cart.item_count === 'number') {
            count = payload.cart.item_count;
          } else if (window.KK.cart && window.KK.cart.state) {
            count = window.KK.cart.state.item_count;
          }
          if (count != null) this._renderCount(count);
        }.bind(this));
      }
    }

    _renderCount(count) {
      if (!this.cartCount) return;
      this.cartCount.textContent = count;
      this.cartCount.hidden = count < 1;
      var label = this.cartCount.getAttribute('data-count-label');
      if (label) this.cartCount.setAttribute('aria-label', label.replace('{{count}}', count));
    }

    /* ── Predictive search ── */
    _wireSearch() {
      var btn = this.querySelector('[data-header-search]');
      if (btn && window.KK && window.KK.pubsub) {
        btn.addEventListener('click', function () {
          window.KK.pubsub.publish('kk:drawer:open', { id: 'search' });
        });
      }
    }

    /* ── Mobile drawer (native <dialog>) ── */
    _wireMobileDrawer() {
      this.dialog = this.querySelector('[data-mobile-drawer]');
      this.openBtn = this.querySelector('[data-mobile-open]');
      if (!this.dialog || !this.openBtn) return;

      var supportsDialog = typeof this.dialog.showModal === 'function';

      this.openBtn.addEventListener('click', function () {
        this._lastFocus = document.activeElement;
        if (supportsDialog) this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
        this.openBtn.setAttribute('aria-expanded', 'true');
        document.documentElement.style.setProperty('overflow', 'hidden');
      }.bind(this));

      var closers = this.dialog.querySelectorAll('[data-mobile-close]');
      for (var i = 0; i < closers.length; i++) {
        closers[i].addEventListener('click', this._closeDrawer.bind(this));
      }
      // Click on backdrop (dialog itself) closes.
      this.dialog.addEventListener('click', function (e) {
        if (e.target === this.dialog) this._closeDrawer();
      }.bind(this));
      this.dialog.addEventListener('close', function () {
        this.openBtn.setAttribute('aria-expanded', 'false');
        document.documentElement.style.removeProperty('overflow');
        if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
      }.bind(this));

      // Accordion toggles inside the drawer (parent items with children).
      var toggles = this.dialog.querySelectorAll('[data-mobile-toggle]');
      for (var t = 0; t < toggles.length; t++) {
        toggles[t].addEventListener('click', function (e) {
          var btn = e.currentTarget;
          var expanded = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          var panel = btn.nextElementSibling;
          if (panel) panel.hidden = expanded;
        });
      }
    }

    _closeDrawer() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.openBtn.setAttribute('aria-expanded', 'false');
        document.documentElement.style.removeProperty('overflow');
        if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
      }
    }
  }

  if (!customElements.get('site-header')) {
    customElements.define('site-header', SiteHeader);
  }
})();
